import React, { useState, useEffect, useCallback } from 'react';
import { KpiCard, ModuleHeader, toneColor } from '@/design-system/dna';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Wallet, CreditCard, Receipt, TrendingUp, AlertTriangle,
  CheckCircle2, Clock, RefreshCw, Send, Eye, Building2,
  FileText, BarChart3, PieChart, Users, AlertCircle, Banknote,
  ChevronRight, ArrowRight, Sparkles, Activity, Zap, Scale,
  ArrowLeftRight, Target
} from 'lucide-react';
import { cn } from '../../../lib/utils';

const API_URL = window.location.origin || '';
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } };
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] } } };
const fmtCur = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);
const fmtCurFull = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

// ── MISSION STATUS ───────────────────────────

const FinancialMission = ({ data, onRefresh }) => {
  const inad = data.valor_inadimplente || 0;
  const status = inad > 10000 ? 'critico' : inad > 0 ? 'atencao' : 'estavel';
  const cfg = { estavel: { l: 'Saudável', c: '#10B981' }, atencao: { l: 'Atenção', c: '#F59E0B' }, critico: { l: 'Crítico', c: '#EF4444' } }[status];

  return (
    <motion.div variants={fadeUp} data-testid="financial-mission">
      <ModuleHeader kicker="Financeiro" title="Financeiro 360" icon={Wallet} testid="financeiro-header"
        subtitle={`Saúde financeira: ${cfg.l} · atualizado às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
        actions={<Button onClick={onRefresh} data-testid="financeiro-refresh" className="dna-mh-primary"><RefreshCw className="h-4 w-4" /> Atualizar</Button>} />
    </motion.div>
  );
};

// ── KPI CARD ─────────────────────────────────

const KPICard = ({ title, value, sub, icon, color, onClick }) => (
  <KpiCard id={`fin-kpi-${title?.toLowerCase().replace(/\s/g, '-')}`} icon={icon} label={title} value={value} hint={sub} color={toneColor(color)} onClick={onClick} />
);

// ── PIPELINE STEP ────────────────────────────

const PipelineStep = ({ label, count, value, icon: Icon, color, active }) => (
  <div className={cn("flex-1 relative")} data-testid={`pipeline-${label?.toLowerCase()}`}>
    <div className={cn("rounded-xl p-4 text-center border transition-all",
      active ? 'shadow-md' : 'opacity-80')}
      style={{ background: `${color}08`, borderColor: `${color}20` }}>
      <Icon className="h-5 w-5 mx-auto mb-2" style={{ color }} />
      <p className="text-lg font-bold text-slate-800">{count}</p>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">{label}</p>
      {value !== undefined && <p className="text-[11px] font-semibold mt-1" style={{ color }}>{fmtCur(value)}</p>}
    </div>
  </div>
);

const PipelineArrow = () => (
  <div className="flex items-center px-1 text-slate-300">
    <ChevronRight className="h-5 w-5" />
  </div>
);

// ── INSIGHT ITEM ─────────────────────────────

const InsightItem = ({ text, action, color, onClick }) => (
  <button onClick={onClick} className="w-full p-3.5 rounded-xl text-left border-l-[3px] hover:translate-x-0.5 transition-all" style={{ borderLeftColor: color, background: `${color}06` }}>
    <p className="text-[12px] text-slate-600 leading-relaxed">{text}</p>
    {action && <span className="text-[11px] font-semibold mt-1.5 flex items-center gap-1" style={{ color }}>{action} <ArrowRight className="h-3 w-3" /></span>}
  </button>
);

// ── QUICK ACTION ─────────────────────────────

const QuickAction = ({ icon: Icon, label, color, onClick }) => (
  <motion.button variants={fadeUp} onClick={onClick} data-testid={`fin-action-${label?.toLowerCase().replace(/\s/g, '-')}`}
    className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-white border border-slate-100 hover:shadow-md hover:-translate-y-[1px] transition-all"
    whileHover={{ boxShadow: `0 4px 20px ${color}15` }}>
    <div className="p-1.5 rounded-lg" style={{ background: `${color}12` }}>
      <Icon className="h-4 w-4" style={{ color }} />
    </div>
    <span className="text-[13px] font-medium text-slate-600">{label}</span>
  </motion.button>
);

// ── RADAR BLOCK ──────────────────────────────

const RadarBlock = ({ title, icon: Icon, color, items, onClick }) => {
  const p = { green: { bg: '#F0FDF9', border: '#D1FAE5', accent: '#059669' }, rose: { bg: '#FFF5F6', border: '#FFE4E6', accent: '#E11D48' }, amber: { bg: '#FFFBEB', border: '#FEF3C7', accent: '#D97706' }, purple: { bg: '#FAF5FF', border: '#EDE9FE', accent: '#7B3F98' } }[color] || {};
  return (
    <motion.div variants={fadeUp} onClick={onClick}
      className="rounded-2xl p-5 cursor-pointer hover:-translate-y-[1px] hover:shadow-md transition-all border"
      style={{ background: p.bg, borderColor: p.border }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg" style={{ background: `${p.accent}10` }}><Icon className="h-4 w-4" style={{ color: p.accent }} /></div>
          <span className="text-[14px] font-semibold text-slate-700">{title}</span>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>
      <div className="space-y-2.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between">
            <span className="text-[12px] text-slate-500">{it.label}</span>
            <span className="text-[14px] font-bold tabular-nums" style={{ color: it.value > 0 ? p.accent : '#94A3B8' }}>{it.formatted || it.value}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════

export default function Financeiro360() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const h = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/v2/financeiro/resumo`, { headers: h });
      setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="space-y-5 animate-pulse" data-testid="financeiro-loading">
        <div className="h-12 rounded-2xl bg-slate-100" />
        <div className="grid grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <div key={i} className="h-28 rounded-2xl bg-slate-100" />)}</div>
        <div className="h-24 rounded-2xl bg-slate-100" />
        <div className="grid grid-cols-2 gap-4">{[...Array(2)].map((_, i) => <div key={i} className="h-44 rounded-2xl bg-slate-100" />)}</div>
      </div>
    );
  }

  const d = data || {};
  const tit = d.titulos || {};
  const dp = d.dnapay || {};
  const recebida = d.receita_recebida || 0;
  const prevista = d.receita_prevista || 0;
  const inadPct = d.inadimplencia_percentual || 0;
  const inadVal = d.valor_inadimplente || 0;
  const aberto = prevista - recebida;
  const ticketMedio = tit.total > 0 ? prevista / tit.total : 0;

  // Insights
  const insights = [];
  if (inadVal > 0) insights.push({ text: `${fmtCur(inadVal)} em inadimplência detectada. Ação de cobrança recomendada.`, action: 'Abrir cobrança', color: '#E11D48', path: '/v2/financeiro/dnapay/remessas' });
  if (tit.vencidos > 0) insights.push({ text: `${tit.vencidos} títulos vencidos aguardando ação.`, action: 'Ver títulos', color: '#D97706', path: '/v2/financeiro/titulos' });
  if (tit.pendentes > 0) insights.push({ text: `${tit.pendentes} títulos pendentes totalizam ${fmtCur(aberto)}.`, action: 'Ver pendentes', color: '#2563EB', path: '/v2/financeiro/titulos' });
  if (dp.titulos > 0) insights.push({ text: `${dp.titulos} títulos no DNA Pay para conciliação.`, action: 'Conciliar', color: '#7B3F98', path: '/v2/financeiro/conciliacao' });
  if (insights.length === 0) insights.push({ text: 'Financeiro saudável. Sem alertas no momento.', color: '#059669' });

  return (
    <motion.div className="space-y-5" initial="hidden" animate="visible" variants={stagger} data-testid="financeiro-360">

      {/* 1. MISSION STATUS */}
      <FinancialMission data={d} onRefresh={load} />

      {/* 2. KPIs */}
      <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" variants={stagger}>
        <KPICard title="Receita Recebida" value={fmtCur(recebida)} sub={`de ${fmtCur(prevista)}`} icon={CheckCircle2} color="green" onClick={() => navigate('/v2/financeiro/titulos')} />
        <KPICard title="Receita Prevista" value={fmtCur(prevista)} sub={`${tit.total || 0} títulos`} icon={Target} color="blue" />
        <KPICard title="Em Aberto" value={fmtCur(aberto)} sub={`${tit.pendentes || 0} pendentes`} icon={Clock} color="amber" onClick={() => navigate('/v2/financeiro/titulos')} />
        <KPICard title="Vencidos" value={tit.vencidos || 0} sub={fmtCur(inadVal)} icon={AlertTriangle} color={tit.vencidos > 0 ? 'rose' : 'green'} onClick={() => navigate('/v2/financeiro/inadimplentes')} />
        <KPICard title="Ticket Médio" value={fmtCurFull(ticketMedio)} icon={Banknote} color="purple" />
        <KPICard title="Inadimplência" value={`${inadPct}%`} sub={fmtCur(inadVal)} icon={AlertCircle} color={inadPct > 5 ? 'rose' : 'cyan'} />
      </motion.div>

      {/* 3. PIPELINE */}
      <motion.div variants={fadeUp}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Fluxo Financeiro</span>
        </div>
        <div className="flex items-center gap-1 rounded-2xl p-4 bg-white border border-slate-100 shadow-sm" data-testid="financial-pipeline">
          <PipelineStep label="Planos" count={0} icon={FileText} color="#7B3F98" active />
          <PipelineArrow />
          <PipelineStep label="Contratos" count={0} icon={Receipt} color="#2563EB" active />
          <PipelineArrow />
          <PipelineStep label="Títulos" count={tit.total || 0} value={prevista} icon={CreditCard} color="#D97706" active />
          <PipelineArrow />
          <PipelineStep label="Pagamentos" count={tit.pagos || 0} value={recebida} icon={CheckCircle2} color="#059669" active />
          <PipelineArrow />
          <PipelineStep label="NFS-e" count={0} icon={Scale} color="#0891B2" />
        </div>
      </motion.div>

      {/* 4. RADAR + INSIGHTS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <motion.div className="lg:col-span-2 grid grid-cols-2 gap-3" variants={stagger}>
          <RadarBlock title="Cobrança" icon={AlertCircle} color="rose"
            items={[
              { label: 'Títulos vencidos', value: tit.vencidos || 0 },
              { label: 'Valor inadimplente', value: 0, formatted: fmtCur(inadVal) },
              { label: 'Taxa inadimplência', value: 0, formatted: `${inadPct}%` },
            ]}
            onClick={() => navigate('/v2/financeiro/dnapay/remessas')} />
          <RadarBlock title="DNA Pay" icon={Banknote} color="green"
            items={[
              { label: 'Títulos', value: dp.titulos || 0 },
              { label: 'Pagamentos', value: dp.pagamentos || 0 },
              { label: 'Conciliados', value: 0, formatted: '0' },
            ]}
            onClick={() => navigate('/v2/financeiro/dnapay')} />
          <RadarBlock title="Fiscal" icon={Scale} color="purple"
            items={[
              { label: 'NFS-e emitidas', value: 0 },
              { label: 'Pendentes', value: 0 },
              { label: 'Erros', value: 0 },
            ]}
            onClick={() => navigate('/v2/financeiro/fiscal')} />
          <RadarBlock title="Conciliação" icon={ArrowLeftRight} color="amber"
            items={[
              { label: 'Recebido', value: 0, formatted: fmtCur(recebida) },
              { label: 'Conciliado', value: 0, formatted: fmtCur(0) },
              { label: 'Diferença', value: 0, formatted: fmtCur(recebida) },
            ]}
            onClick={() => navigate('/v2/financeiro/conciliacao')} />
        </motion.div>

        {/* INSIGHTS */}
        <motion.div variants={fadeUp}>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-3.5 w-3.5 text-violet-500" />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">IA Financeira</span>
          </div>
          <div className="rounded-2xl p-4 space-y-3 bg-white border border-slate-100 shadow-sm">
            {insights.map((ins, i) => (
              <InsightItem key={i} text={ins.text} action={ins.action} color={ins.color} onClick={() => ins.path && navigate(ins.path)} />
            ))}
          </div>
        </motion.div>
      </div>

      {/* 5. QUICK ACTIONS */}
      <motion.div variants={fadeUp}>
        <div className="flex items-center gap-2 mb-3">
          <Zap className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Ações Financeiras</span>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <QuickAction icon={Receipt} label="Gerar Títulos" color="#D97706" onClick={() => navigate('/v2/financeiro/gerar-titulos')} />
          <QuickAction icon={Send} label="Remessa CNAB" color="#E11D48" onClick={() => navigate('/v2/financeiro/dnapay/remessas')} />
          <QuickAction icon={Banknote} label="DNA Pay" color="#059669" onClick={() => navigate('/v2/financeiro/dnapay')} />
          <QuickAction icon={Scale} label="Emitir NFS-e" color="#0891B2" onClick={() => navigate('/v2/financeiro/fiscal')} />
          <QuickAction icon={BarChart3} label="Relatórios" color="#7B3F98" onClick={() => navigate('/v2/financeiro/relatorios')} />
          <QuickAction icon={Building2} label="Contas" color="#2563EB" onClick={() => navigate('/v2/financeiro/contas')} />
        </div>
      </motion.div>
    </motion.div>
  );
}
